import type { z } from 'zod'
import { ApiError } from './errors'

/**
 * Centralized fetch wrapper for lionfish's own backend API.
 *
 * - Never carries third-party credentials (e.g. Metabase API keys) — those
 *   must stay server-side behind whatever endpoint this calls.
 * - Defaults to `credentials: 'same-origin'` so cookies are never sent
 *   cross-origin.
 * - Validates every JSON response against a caller-provided Zod schema
 *   before returning it.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'https://api.dev-n26.com'

export type ApiFetchInit<T extends z.ZodTypeAny> = Omit<
  RequestInit,
  'body' | 'credentials'
> & {
  body?: Record<string, unknown> | null
  schema: T
}

const parseBody = async (response: Response): Promise<unknown> => {
  const contentType = response.headers.get('content-type') ?? ''
  if (response.status === 204 || !contentType.includes('json')) return null
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

export async function apiFetch<T extends z.ZodTypeAny>(
  path: string,
  init: ApiFetchInit<T>
): Promise<z.infer<T>> {
  const { body, schema, ...restInit } = init
  const headers = new Headers(restInit.headers)
  headers.set('Accept', 'application/json')
  if (body != null) headers.set('Content-Type', 'application/json')

  const response = await fetch(API_BASE + path, {
    ...restInit,
    headers,
    credentials: 'same-origin',
    ...(body != null ? { body: JSON.stringify(body) } : {}),
  })

  const responseBody = await parseBody(response)

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, responseBody)
  }

  const parsed = schema.safeParse(responseBody)
  if (!parsed.success) {
    throw new ApiError(response.status, 'Response did not match expected schema', {
      issues: parsed.error.issues,
      body: responseBody,
    })
  }
  return parsed.data
}
